import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { Subject } from 'rxjs';
import { Permissions } from '../enums';

import { MemoryOAuthStorage, SessionSnapshot } from './memory-oauth-storage';
import { TabSessionChannel } from './tab-session-channel';

/**
 * Renew this long before expiry, so a request is never sent with a token that is about to lapse.
 */
const RENEWAL_MARGIN_MS = 60_000;

/**
 * Marks that an interactive sign-in has already been tried in this tab.
 *
 * Without it a Cardinal that refuses the login for a configuration reason -- an unknown client, a
 * redirect uri that does not match -- would produce an endless redirect loop instead of an error
 * anybody can see.
 */
const INTERACTIVE_ATTEMPT_KEY = 'login_interactive_attempted';

/**
 * The one route that renders without a session.
 *
 * Exported because two places have to agree on it and they are far apart: the route table, and the
 * `postLogoutRedirectUri` that is registered in Cardinal. A literal in both is a literal that drifts.
 */
export const SIGNED_OUT_ROUTE = '/auth/signed-out';

/**
 * Where a sign-in lands when it has nowhere better to go.
 *
 * `/` is enough here: this shell's children carry an empty-path entry that redirects to
 * `configuration`, so the router resolves the root on its own. It is a named constant anyway,
 * because that is a property of this route table rather than a general truth -- Assets has no such
 * entry and has to name its start route outright.
 */
export const START_ROUTE = '/';

/**
 * The authentication, against Cardinal, on the authorization code flow with PKCE.
 *
 * The library's own `loadDiscoveryDocumentAndLogin` is deliberately not used. It answers a missing
 * token with an immediately visible, interactive redirect, and it is built around renewal through a
 * hidden iframe. Both are what this application moved away from, so the three steps it would take
 * are orchestrated here instead:
 *
 *   1. **Coming back from Cardinal** -- exchange the code, and fall back to an interactive attempt
 *      when `prompt=none` found no session.
 *   2. **Starting without a session** -- ask the other tabs first. That is what makes a reload and
 *      "open in a new tab" render straight away rather than bouncing through Cardinal.
 *   3. **Nobody answered** -- a **top-level redirect** with `prompt=none`. A top-level navigation is
 *      not a third-party context, so Cardinal's session cookie travels regardless of the browser's
 *      third-party cookie policy. That is the whole reason there is no iframe here.
 *
 * What the library still does is its protocol work: discovery, the PKCE challenge, `state` and
 * `nonce`, the code exchange, ID token validation against JWKS, and the refresh request. What is
 * ours are the three decisions it has no opinion we share about -- where the tokens live
 * (MemoryOAuthStorage), how a tab without one gets a session (TabSessionChannel), and when a session
 * is renewed (AuthInterceptor).
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private _profileLoadedSubject = new Subject<any>();
  private _userProfile: any;
  private _appMetaData: any = {};
  private _renewal: Promise<boolean> | null = null;
  private _startingRenewal = false;
  private _initializing = false;
  private _redirecting = false;

  public profileLoaded$ = this._profileLoadedSubject.asObservable();

  constructor(
    private _oauthService: OAuthService,
    private _storage: MemoryOAuthStorage,
    private _channel: TabSessionChannel,
    private _router: Router
  ) {

  }

  /**
   * Where to come back to once the user is signed in.
   *
   * **Both directions strip Cardinal's answer, and that is not belt-and-braces.** The guard writes
   * the router url here on every navigation, so after a failed `prompt=none` it writes the error
   * response itself -- which then travelled into `state` as the sign-in target and brought the user
   * straight back to the failure. Filtering on write stops that happening.
   *
   * Filtering on read is for the values already sitting in browsers from before this was fixed:
   * `localStorage` keeps them until something overwrites them, and a poisoned one there is what kept
   * Assets broken while Ascent, whose entry happened to be empty, worked.
   */
  public set redirectUrl(url: string) {
    localStorage.setItem('redirectUrl', withoutAuthorizationResponseUrl(url));
  }

  public get redirectUrl(): string {
    return withoutAuthorizationResponseUrl(localStorage.getItem('redirectUrl') || '');
  }

  public isInitialized(): boolean {
    return !!this._userProfile;
  }

  public isAuthenticated(): boolean {
    return this._oauthService.hasValidAccessToken();
  }

  public getAccessToken(): string {
    return this._oauthService.getAccessToken();
  }

  public getUsername(): string {
    return this._userProfile?.email;
  }

  public getUserId(): number | undefined {
    return parseInt(this._appMetaData?.userId);
  }

  public getUserThumbnail(): string {
    return this._userProfile?.picture;
  }

  public getUserCulture(): string {
    return this._userProfile?.PreferredLanguage || 'de-DE';
  }

  public hasValidAccountId(): boolean {
    return !!this._appMetaData?.AccountId;
  }

  public showTenantControls(): boolean {
    return !!this._appMetaData?.TenantId
      && this._appMetaData.TenantId === this._appMetaData.EshavaTenantReferenceId;
  }

  public getAccountId(): number {
    return this._appMetaData?.AccountId;
  }

  public hasPermission(permissions: Permissions[]): boolean {
    if (!permissions || permissions.length === 0) {
      return true;
    }

    if (!this._appMetaData?.PermissionString) {
      return false;
    }

    for (const p of permissions) {
      if (this._appMetaData.PermissionString.includes(String.fromCharCode(p))) {
        return true;
      }
    }

    return false;
  }

  /**
   * Whether the session is close enough to expiry to be worth renewing.
   *
   * Evaluated on demand rather than on a timer: a timer in a backgrounded tab is throttled, so a
   * token that lapsed while the tab was hidden must not look valid the moment it comes back.
   */
  public needsRenewal(): boolean {
    const expiresAt = this._oauthService.getAccessTokenExpiration();

    return !expiresAt || expiresAt - RENEWAL_MARGIN_MS <= Date.now();
  }

  /**
   * Renews through the refresh token. No iframe, and no redirect -- nothing on screen moves, which
   * is what lets a half-filled form survive it.
   *
   * **One renewal at a time.** A screen opens with several requests in flight and each of them now
   * asks for a fresh token. Without sharing the attempt they would each spend the refresh token, and
   * all but the first would be spending one Cardinal has just rotated away.
   *
   * **And a renewal must never ask for a renewal.** The library sends the refresh request from
   * inside `renewOnce`, **synchronously** -- `refreshToken` subscribes to the HttpClient call in the
   * executor of the promise it returns, so the interceptor chain runs before `renewOnce` has even
   * suspended, and long before `_renewal` has been assigned. A request seen in that window found no
   * renewal in flight, started another one, sent another request, and the stack ran out:
   *
   *     ERROR RangeError: Maximum call stack size exceeded
   *         at e.renewOnce ... at e.renew
   *
   * The crash takes zone.js's microtask queue with it, which is why the application stopped
   * reacting to clicks until a reload. `_startingRenewal` covers exactly that window; the reason
   * the window was reachable at all -- Cardinal in `ServiceOrigins` -- is fixed in `main.ts`.
   */
  public renew(): Promise<boolean> {
    if (this._renewal) {
      return this._renewal;
    }

    // Re-entered from within the refresh request itself. That request carries the refresh token and
    // needs no access token, so `false` is the truthful answer: nothing was renewed, and the caller
    // sends what it has.
    if (this._startingRenewal) {
      return Promise.resolve(false);
    }

    this._startingRenewal = true;

    try {
      this._renewal = this.renewOnce().finally(() => {
        this._renewal = null;
      });
    } finally {
      this._startingRenewal = false;
    }

    return this._renewal;
  }

  /**
   * Signs in from the signed-out page -- **interactively**.
   *
   * `prompt=none` would find no Cardinal session, since signing out just ended it, and would bounce
   * the user through a failed attempt before the login screen for nothing.
   */
  public signInInteractively(): void {
    this._storage.clearPendingLogin();
    sessionStorage.removeItem(INTERACTIVE_ATTEMPT_KEY);
    this.signIn(START_ROUTE, true);
  }

  public signOut(): void {
    this._channel.publishSignOut();
    this._storage.clearPendingLogin();
    sessionStorage.removeItem(INTERACTIVE_ATTEMPT_KEY);
    this._oauthService.logOut();
  }

  /**
   * Sends the user back to Cardinal, interactively.
   *
   * Reached when a request comes back 401 that a renewal could not save, which means the session is
   * gone rather than stale -- so `prompt=none` would only add a bounce before the login screen.
   */
  public redirectToSignIn(url: string): void {
    // While the initialisation is still running, it decides where this tab goes. The guard asks for
    // a sign-in synchronously -- canLoad and canActivateChild both do, before start() has even
    // finished loading the discovery document -- and two redirects racing means the loser's target
    // url is the one that survives in `state`.
    if (this._initializing) {
      return;
    }

    this._storage.clearSession();
    this._storage.clearPendingLogin();
    this.redirectUrl = url;
    this.signIn(url, true);
  }

  /**
   * Configures the library and establishes a session. Called once, from the shell component.
   */
  public configure(authConfig: AuthConfig): void {
    this._oauthService.configure(authConfig);
    this._oauthService.tokenValidationHandler = new JwksValidationHandler();
    this._channel.listen(() => this.offerSession(), () => this.onSignedOutElsewhere());

    this._initializing = true;
    void this.start().finally(() => {
      this._initializing = false;
    });
  }

  private async start(): Promise<void> {
    // Loaded even on the signed-out page: the sign-in button there needs the authorization endpoint,
    // and initCodeFlow would otherwise wait for a discovery event that never arrives.
    await this._oauthService.loadDiscoveryDocument();

    // Nothing else is reachable without a session, so landing anywhere but here starts a sign-in.
    // That is why signing out needs a page of its own -- without it the user is returned to the
    // application, the application asks Cardinal again, and being signed out lasts one redirect.
    if (isSignedOutRoute()) {
      // Ends the shell's loading state. The page renders without a profile and there will not be
      // one until the user signs in again.
      this._profileLoadedSubject.next(null);

      return;
    }

    // Coming back from Cardinal. The code arrives as a query parameter, not in the fragment -- which
    // is the visible difference from the implicit flow and the reason no token reaches the history.
    if (hasAuthorizationResponse()) {
      await this.completeSignIn();

      return;
    }

    // A tab that starts has no tokens, because they were never persisted. Ask the others before
    // sending anyone anywhere: for a link opened in a new tab the originating tab is open by
    // definition, so this is the common case and it costs no redirect at all.
    const shared = await this._channel.requestSession();

    if (shared && this.adoptSession(shared)) {
      await this.loadUserProfile();

      // A session handed over by another tab still has to land somewhere. Only the callback path
      // navigated at first, so a tab that took its session from a neighbour stayed on whatever
      // address it was opened with -- and on the root that is a shell with an empty outlet.
      this.navigateToTarget();

      return;
    }

    this.signIn(currentUrl(), false);
  }

  private async completeSignIn(): Promise<void> {
    try {
      await this._oauthService.tryLoginCodeFlow();
    } catch {
      // Most likely `login_required`: prompt=none found no session at Cardinal. That is the normal
      // first visit rather than a failure, so ask again -- this time with a login screen, and aimed
      // where the failed attempt was aimed.
      clearAuthorizationResponse();
      this.retryInteractively();

      return;
    }

    if (!this._oauthService.hasValidAccessToken()) {
      this.retryInteractively();

      return;
    }

    clearAuthorizationResponse();
    sessionStorage.removeItem(INTERACTIVE_ATTEMPT_KEY);
    this._storage.clearPendingLogin();
    this._channel.publish(this._storage.snapshot());

    await this.loadUserProfile();

    this.navigateToTarget();
  }

  private retryInteractively(): void {
    this._storage.clearPendingLogin();

    if (sessionStorage.getItem(INTERACTIVE_ATTEMPT_KEY)) {
      // An interactive attempt came back refused as well. That is a configuration problem -- an
      // unknown client id, a redirect uri Cardinal does not have, an application still registered as
      // confidential -- and redirecting again would turn it into a loop instead of an error.
      sessionStorage.removeItem(INTERACTIVE_ATTEMPT_KEY);
      console.error('Sign-in was refused by Cardinal. Check the application record: authentication type, client id and redirect uris.');

      return;
    }

    // **The target comes from `state` first.** The library reads it out of Cardinal's answer before it
    // looks at the error, so a failed `prompt=none` still says where it was aimed. `redirectUrl` is
    // no substitute here: the guard has already run on this load and written the callback address
    // into it -- which is how a reload after the Cardinal session lapsed ended on the start page
    // rather than the page that was reloaded.
    this.signIn(decodeState(this._oauthService.state) || this.redirectUrl || currentUrl(), true);
  }

  /**
   * Starts the redirect to Cardinal.
   *
   * The target url travels in `state`, so a deep link comes back to where it was aimed even though
   * nothing about this tab survives the redirect. `redirectUrl` is kept in step as the fallback for
   * the one case `state` cannot cover -- a login the user started in a different tab.
   */
  private signIn(targetUrl: string, interactive: boolean): void {
    // A tab leaves for Cardinal once. The redirect is asynchronous -- the library builds the url,
    // creates the PKCE challenge and only then navigates -- so everything that runs in the meantime
    // could start a second one, and the second would overwrite the nonce the first is waiting for.
    if (this._redirecting) {
      return;
    }

    this._redirecting = true;

    // The last gate before the target reaches `state`. Every caller is filtered by now, but this is
    // the only place that writes it, so this is the place that has to be right.
    const target = withoutAuthorizationResponseUrl(targetUrl);

    this.redirectUrl = target;

    if (interactive) {
      sessionStorage.setItem(INTERACTIVE_ATTEMPT_KEY, '1');
    }

    this._oauthService.initCodeFlow(target, interactive ? {} : { prompt: 'none' });
  }

  private navigateToTarget(): void {
    const target = targetAfterSignIn(this._oauthService.state, this.redirectUrl);

    this.redirectUrl = '';

    void this._router.navigateByUrl(target);
  }

  private async renewOnce(): Promise<boolean> {
    if (!this._oauthService.getRefreshToken()) {
      return false;
    }

    try {
      await this._oauthService.refreshToken();

      // Cardinal rotates refresh tokens, so the other tabs are now holding a spent one. Handing them
      // the new set is what keeps a shared session shared.
      this._channel.publish(this._storage.snapshot());

      return true;
    } catch {
      // A refused refresh token is not an error to report: it means the session is over, and the
      // 401 that follows is what says so.
      return false;
    }
  }

  private adoptSession(snapshot: SessionSnapshot): boolean {
    this._storage.restore(snapshot);

    if (this._oauthService.hasValidAccessToken()) {
      return true;
    }

    // An offer that arrived expired is worse than none -- it would make this tab believe it is
    // signed in and skip the redirect.
    this._storage.clearSession();

    return false;
  }

  private offerSession(): SessionSnapshot | null {
    return this._oauthService.hasValidAccessToken() ? this._storage.snapshot() : null;
  }

  private onSignedOutElsewhere(): void {
    this._storage.clearSession();
    this._userProfile = null;
    this._appMetaData = {};
  }

  private async loadUserProfile(): Promise<void> {
    const userProfile = await this._oauthService.loadUserProfile() as any;

    // The claims come from the userinfo endpoint rather than from the token, and that is deliberate:
    // the token's claims are a snapshot taken when the user signed in, carried in the Cardinal
    // session, so they go stale when an assignment changes mid-session.
    this._userProfile = userProfile.info;
    this._appMetaData = JSON.parse(this._userProfile.app_metadata || '{}');
    this._profileLoadedSubject.next(this._userProfile);
  }
}

function isSignedOutRoute(): boolean {
  return window.location.pathname === SIGNED_OUT_ROUTE;
}

/** Whether this load is Cardinal answering an authorization request. */
function hasAuthorizationResponse(): boolean {
  const parameters = new URLSearchParams(window.location.search);

  return parameters.has('code') || parameters.has('error');
}

/**
 * Where this tab is, as a target to come back to -- **never carrying Cardinal's answer**.
 *
 * `tryLoginCodeFlow` cleans `code`, `scope`, `session_state` and `state` out of the address on its
 * way through, but leaves `error`, `error_description`, `error_uri` and `iss` standing. Reading the
 * raw url then put the whole failed response into `state` as the target, the sign-in came back to
 * it, saw `?error=` again and asked once more -- until the loop guard stopped it. The address bar
 * kept a url nothing could escape from, and Cardinal held it as its return url.
 */
function currentUrl(): string {
  return window.location.pathname + withoutAuthorizationResponse(window.location.search);
}

/** The parameters Cardinal answers an authorization request with. */
const RESPONSE_PARAMETERS: readonly string[] =
  ['code', 'state', 'scope', 'session_state', 'iss', 'error', 'error_description', 'error_uri'];

/**
 * Strips Cardinal's answer out of a relative url -- path and query together.
 *
 * The counterpart to `withoutAuthorizationResponse`, which takes a bare query string. Both exist
 * because the two live in different shapes: `window.location.search` is a query, and what the guard
 * hands over is a whole router url.
 */
export function withoutAuthorizationResponseUrl(url: string): string {
  const separator = url.indexOf('?');

  return separator === -1
    ? url
    : url.substring(0, separator) + withoutAuthorizationResponse(url.substring(separator));
}

/** Exported for its test: this is the strip that a failed sign-in used to get past. */
export function withoutAuthorizationResponse(search: string): string {
  const parameters = new URLSearchParams(search);

  RESPONSE_PARAMETERS.forEach(name => parameters.delete(name));

  const query = parameters.toString();

  return query ? `?${query}` : '';
}

/**
 * Takes Cardinal's answer out of the address bar.
 *
 * The library does this for the parameters it consumes, and only for those. What is left over after
 * a failed attempt has to go too, or the next load reads it as a fresh answer.
 */
function clearAuthorizationResponse(): void {
  const cleaned = window.location.pathname
    + withoutAuthorizationResponse(window.location.search)
    + window.location.hash;

  history.replaceState(null, '', cleaned);
}

/**
 * Where to go once a session exists.
 *
 * Three cases, and the third is why this is a function rather than a line: a callback carries the
 * address in `state`; a session handed over by another tab carries none, so what the guard last
 * wrote is used; and neither having anything to say means the application opens where it opens.
 *
 * `/` counts as nothing to say. It is the address a browser lands on, not a screen -- see
 * START_ROUTE.
 *
 * Exported for its test.
 */
export function targetAfterSignIn(state: string | undefined, redirectUrl: string): string {
  const target = decodeState(state) || redirectUrl;

  return target && target !== '/' ? target : START_ROUTE;
}

/**
 * The library stores the user state as
 `nonce + separator + encodeURIComponent(state)` and hands it
 * back still encoded, so it has to be decoded here.
 */
function decodeState(state: string | undefined): string {
  if (!state) {
    return '';
  }

  try {
    return decodeURIComponent(state);
  } catch {
    return '';
  }
}
